import {useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import type {FunctionComponent, SyntheticEvent} from 'react'
import React from 'react'
import {PersentageIcon} from '~/icons/sale-events/percentageIcon'
import {SnowIcon} from '~/icons/sale-events/snowIcon'
import {ThunderDealIcon} from '~/icons/thunder-deal-icon'
import {getTranslation} from '~/localization/translation'
import type {RootLoaderResponse} from '~/types'
import {cloudfrontBaseUrl} from '~/utils/constants'

interface SaleEventBadgeProps {
  redirectUrl: string
  handleCardClick: (e: SyntheticEvent) => void
  brand: string
  index: number
  placement?: 'brg-card' | 'spec-card' | 'eu-card' | 'top-picks-card'
}
const longTextLoclaes = ['de', 'fr', 'it']

const SaleEventBadge: FunctionComponent<SaleEventBadgeProps> = ({
  redirectUrl,
  handleCardClick,
  brand,
  index,
  placement = 'brg-card',
}) => {
  const {language, featureFlags, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)

  const saleEventKey = featureFlags?.enablePrimeDayIcon
    ? 'primeDay'
    : featureFlags?.enableBlackFridayIcon
      ? 'blackFriday'
      : featureFlags?.enableCyberMondayIcon
        ? 'cyberMonday'
        : featureFlags?.enableChristmasBadge
          ? 'christmas'
          : null

  if (saleEventKey === 'primeDay') {
    return (
      <div className="relative mx-auto mt-2 flex flex-row items-center justify-between gap-5 text-sm font-light">
        <a
          className="absolute inset-0"
          href={redirectUrl}
          onClick={handleCardClick}
          aria-label={`view ${brand}`}
          target="_blank"
          rel="sponsored noreferrer"
        ></a>
        <span className="text-sm font-extrabold leading-4">
          {translations.PrimeDay.primeBig}
          <br />
          {translations.PrimeDay.dealDays}
        </span>
        <div className="h-7 w-12">
          <img
            src="/icons/prime-blue-logo.png"
            alt="prime day logo"
            className="h-7 w-12 shrink-0 object-scale-down"
            loading={index > 0 ? 'lazy' : 'eager'}
          />
        </div>
      </div>
    )
  }

  if (saleEventKey === 'blackFriday') {
    return (
      <a
        href={redirectUrl}
        onClick={handleCardClick}
        aria-label={`view ${brand}`}
        target="_blank"
        rel="sponsored noreferrer"
        className={clsx(
          'mx-auto mt-2 flex w-fit flex-row items-center rounded-lg border border-[#FF6200] py-0.5 pl-1 pr-2 font-medium text-[#FF6200]',
          longTextLoclaes.includes(locale.toLowerCase())
            ? 'text-sm'
            : locale.toLowerCase() === 'es'
              ? 'text-[13px]'
              : 'text-base',
        )}
      >
        <div className="h-[24px] w-[30px]">
          <img src={`${cloudfrontBaseUrl}/black-friday/black_friday_icon_orange.webp`} alt="black friday badge" />
        </div>
        {translations.BlackFriday.blackFridayDeal}
      </a>
    )
  }

  if (saleEventKey === 'cyberMonday') {
    return (
      <a
        href={redirectUrl}
        onClick={handleCardClick}
        aria-label={`view ${brand}`}
        target="_blank"
        rel="sponsored noreferrer"
        className={clsx(
          'mx-auto mt-2 flex w-fit flex-row items-center rounded-lg border border-[#FF243E] py-0.5 pl-1 pr-2 font-medium text-[#FF243E]',
          longTextLoclaes.includes(locale.toLowerCase())
            ? 'text-sm'
            : locale.toLowerCase() === 'es'
              ? 'text-[13px]'
              : 'text-base',
        )}
      >
        <ThunderDealIcon />
        {translations.CyberMonday.cyberMondayDeal}
      </a>
    )
  }

  if (saleEventKey === 'christmas') {
    return (
      <a
        href={redirectUrl}
        onClick={handleCardClick}
        aria-label={`view ${brand}`}
        target="_blank"
        rel="sponsored noreferrer"
        className={clsx(
          'mx-auto mt-2 flex w-fit flex-row items-center gap-1.5 rounded-[4px] bg-[#2B4E4A] px-2 text-[#CAFED9] mobile:h-[36px] mobile:text-sm mobile:font-bold',
          placement === 'brg-card' ? 'mt-3 h-[30px] text-xs font-semibold' : 'h-[36px] text-sm font-bold',
        )}
      >
        <PersentageIcon className="mobile:size-[14px]" />
        {translations.Christmas.christmasDeal}
        <SnowIcon className="mobile:size-[18px]" />
      </a>
    )
  }

  return null
}

export default SaleEventBadge
